import { useEffect, useRef } from 'react';

/**
 * Wisps (pontinhos de luz vagando pela escuridão) + luz que segue o cursor
 * — os mesmos dois efeitos da landing page (ver
 * dashboard/web/public/landing/index-v2.html), portados pra cá pra manter
 * a mesma atmosfera "penumbral" no login.
 */
export function LoginAtmosphere() {
  const containerRef = useRef<HTMLDivElement>(null);
  const glowRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const WISP_COUNT = 8;
    const wisps: HTMLDivElement[] = [];

    for (let i = 0; i < WISP_COUNT; i++) {
      const wisp = document.createElement('div');
      wisp.className = 'login__wisp';
      wisp.setAttribute('aria-hidden', 'true');

      const startX = Math.random() * 100;
      const startY = Math.random() * 100;
      const dx = (Math.random() - 0.5) * 220;
      const dy = -(60 + Math.random() * 160);
      const driftDuration = 14 + Math.random() * 16;
      const twinkleDuration = 3 + Math.random() * 4;
      const delay = Math.random() * -20;
      const peak = 0.3 + Math.random() * 0.4;

      wisp.style.left = `${startX}%`;
      wisp.style.top = `${startY}%`;
      wisp.style.setProperty('--wisp-dx', `${dx}px`);
      wisp.style.setProperty('--wisp-dy', `${dy}px`);
      wisp.style.setProperty('--wisp-peak', peak.toFixed(2));
      wisp.style.animationDuration = `${driftDuration}s, ${twinkleDuration}s`;
      wisp.style.animationDelay = `${delay}s, ${delay}s`;

      container.appendChild(wisp);
      wisps.push(wisp);
    }

    return () => {
      for (const wisp of wisps) wisp.remove();
    };
  }, []);

  useEffect(() => {
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

    // O listener vai no .login (o elemento-pai de verdade), não neste
    // overlay (que fica pointer-events:none pra não bloquear o cartão) —
    // eventos do cartão (um irmão deste componente, filho de .login) sobem
    // até .login por bubbling, nunca chegariam aqui se o listener estivesse
    // só no overlay.
    const loginEl = containerRef.current?.parentElement;
    const glow = glowRef.current;
    if (!loginEl || !glow) return;

    let hideTimer: ReturnType<typeof setTimeout> | null = null;

    function onMove(event: PointerEvent) {
      if (!loginEl || !glow) return;
      const rect = loginEl.getBoundingClientRect();
      const xPct = ((event.clientX - rect.left) / rect.width) * 100;
      const yPct = ((event.clientY - rect.top) / rect.height) * 100;
      glow.style.setProperty('--cx', `${xPct}%`);
      glow.style.setProperty('--cy', `${yPct}%`);
      glow.classList.add('is-active');
      if (hideTimer) clearTimeout(hideTimer);
      hideTimer = setTimeout(() => glow.classList.remove('is-active'), 2200);
    }

    function onLeave() {
      if (hideTimer) clearTimeout(hideTimer);
      glow?.classList.remove('is-active');
    }

    loginEl.addEventListener('pointermove', onMove);
    loginEl.addEventListener('pointerleave', onLeave);
    return () => {
      loginEl.removeEventListener('pointermove', onMove);
      loginEl.removeEventListener('pointerleave', onLeave);
      if (hideTimer) clearTimeout(hideTimer);
    };
  }, []);

  return (
    <div className="login__atmosphere" ref={containerRef} aria-hidden="true">
      <div className="login__cursor-glow" ref={glowRef} />
    </div>
  );
}
